import React, { useState, useEffect } from 'react';
import { UserPlus, UserMinus, Clock, User, Laptop, Smartphone, Tablet } from 'lucide-react';
import Modal from './common/Modal';
import axiosInstance from '../utils/axiosConfig';
import { getApiError } from '../utils/apiError';

const TIPO_ICONS = {
  asset: Laptop,
  smartphone: Smartphone,
  tablet: Tablet,
};

const TIPO_LABELS = {
  asset: 'Equipo',
  smartphone: 'Smartphone',
  tablet: 'Tablet',
};

function AssignmentTimeline({ tipo, equipoId, equipoNombre, onClose }) {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHistorial = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/historial-asignaciones/${tipo}/${equipoId}`);
        setHistorial(data);
      } catch (err) {
        // Un error no es lo mismo que "sin registros": se muestra aparte
        setHistorial([]);
        setError(getApiError(err, 'No se pudo cargar el historial de asignaciones'));
      } finally {
        setLoading(false);
      }
    };
    fetchHistorial();
  }, [tipo, equipoId]);

  const Icon = TIPO_ICONS[tipo] || Laptop;

  // Más nuevo arriba
  const sorted = [...historial].reverse();

  return (
    <Modal isOpen={true} onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-2">
          <Icon size={18} />
          Historial de asignaciones — {TIPO_LABELS[tipo]}: {equipoNombre}
        </span>
      </Modal.Header>
      <Modal.Body>
        <div style={{ maxHeight: 'calc(70vh - 120px)', overflowY: 'auto', padding: '4px 0' }}>
          {loading && (
            <div className="space-y-3 skeleton" style={{ padding: '1rem 0' }}>
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-slate-200 rounded w-3/4" />
                    <div className="h-2.5 bg-slate-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-8 text-sm text-red-500">
              {error}
            </div>
          )}

          {!loading && !error && historial.length === 0 && (
            <div className="text-center py-8 text-sm text-slate-400">
              <Clock size={32} className="mx-auto mb-2 opacity-30" />
              No hay registros de asignación para este equipo.
            </div>
          )}

          {!loading && sorted.length > 0 && (
            <div className="relative">
              {/* Línea vertical */}
              <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-slate-200" />

              <div className="space-y-0">
                {sorted.map((item, idx) => {
                  const isAsignado = item.accion === 'asignado';
                  const fecha = new Date(item.fecha);
                  const isFirst = idx === 0;

                  return (
                    <div key={item.id} className="relative flex gap-3 pb-5 last:pb-0">
                      <div className={`relative z-10 flex-shrink-0 w-[30px] h-[30px] rounded-full flex items-center justify-center ${
                        isAsignado
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-amber-100 text-amber-600'
                      }`}>
                        {isAsignado ? <UserPlus size={13} /> : <UserMinus size={13} />}
                      </div>

                      <div className={`flex-1 rounded-lg border px-3 py-2.5 ${
                        isFirst ? 'border-blue-200 bg-blue-50/50' : 'border-slate-100 bg-slate-50/50'
                      }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className={`text-xs font-semibold ${
                              isAsignado ? 'text-emerald-700' : 'text-amber-700'
                            }`}>
                              {isAsignado ? 'Asignado' : 'Desasignado'}
                            </span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <User size={11} className="text-slate-400" />
                              <span className="text-sm text-slate-700 font-medium">
                                {item.empleado_nombre || 'Desconocido'}
                              </span>
                            </div>
                          </div>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap mt-0.5">
                            {fecha.toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        </div>
                        {item.motivo && (
                          <p className="text-xs text-slate-500 mt-1 italic">{item.motivo}</p>
                        )}
                        {item.usuario_nombre && (
                          <p className="text-[11px] text-slate-400 mt-1">por {item.usuario_nombre}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                <span>{historial.length} registro{historial.length !== 1 ? 's' : ''}</span>
                <span>
                  {historial.filter(h => h.accion === 'asignado').length} asignaciones,{' '}
                  {historial.filter(h => h.accion === 'desasignado').length} desasignaciones
                </span>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn-primary" onClick={onClose}>Cerrar</button>
      </Modal.Footer>
    </Modal>
  );
}

export default AssignmentTimeline;
